/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { Tooltip } from '../Tooltip/index.js';
/**
 * Thumb is a local component used to show and modify the values maintained by the Slider.
 * Only one Thumb can be active at a time. A Thumb is made active when the user selects
 * it with pointer. Whenever a Thumb is active, focused, or hovered its tooltip is shown.
 */
export const Thumb = (props) => {
    const { value, index, minVal, maxVal, step, sliderMin, sliderMax, isActive, onThumbActivated, onThumbValueChanged, tooltipProps, thumbProps, disabled, orientation, } = props;
    const thumbRef = React.useRef(null);
    const handleOnKeyboardEvent = React.useCallback((event, keyboardReleased) => {
        if (disabled || event.altKey) {
            return;
        }
        switch (event.key) {
            case 'ArrowLeft':
            case 'ArrowDown':
                onThumbValueChanged(index, Math.max(value - step, minVal), keyboardReleased);
                break;
            case 'ArrowRight':
            case 'ArrowUp':
                onThumbValueChanged(index, Math.min(value + step, maxVal), keyboardReleased);
                break;
            case 'Home':
                onThumbValueChanged(index, minVal, keyboardReleased);
                break;
            case 'End':
                onThumbValueChanged(index, maxVal, keyboardReleased);
                break;
            default:
                return;
        }
        event.preventDefault();
    }, [disabled, onThumbValueChanged, index, value, step, minVal, maxVal]);
    const handlePointerDownOnThumb = React.useCallback(() => {
        !disabled && onThumbActivated(index);
    }, [disabled, index, onThumbActivated]);
    const adjustedValue = React.useMemo(() => {
        if (value < sliderMin) {
            return sliderMin;
        }
        if (value > sliderMax) {
            return sliderMax;
        }
        return value;
    }, [sliderMax, sliderMin, value]);
    const lowPercent = React.useMemo(() => {
        if (sliderMax === sliderMin) {
            return 0;
        }
        return (100.0 * (adjustedValue - sliderMin)) / (sliderMax - sliderMin);
    }, [adjustedValue, sliderMax, sliderMin]);
    const { style, className, ...rest } = thumbProps || {};
    return (React.createElement(Tooltip, { placement: 'top', trigger: (tooltipProps === null || tooltipProps === void 0 ? void 0 : tooltipProps.visible) == null ? 'mouseenter click focus' : undefined, ...tooltipProps },
        React.createElement("div", { ...rest, "data-index": index, ref: thumbRef, style: {
                ...style,
                ...(orientation === 'horizontal'
                    ? { left: `${lowPercent}%` }
                    : { bottom: `${lowPercent}%` }),
            }, className: cx('iui-slider-thumb', { 'iui-active': isActive }, className), role: 'slider', tabIndex: disabled ? undefined : 0, "aria-valuemin": minVal, "aria-valuenow": value, "aria-valuemax": maxVal, "aria-disabled": disabled, onPointerDown: handlePointerDownOnThumb, onKeyDown: (event) => handleOnKeyboardEvent(event, false), onKeyUp: (event) => handleOnKeyboardEvent(event, true) })));
};
